import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Meeting } from '../../types/meetings';

interface MiniCalendarProps {
  meetings: Meeting[];
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
}

export const MiniCalendar: React.FC<MiniCalendarProps> = ({
  meetings,
  selectedDate,
  onDateSelect
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const hasMeeting = (date: Date) => {
    return meetings.some(meeting => 
      isSameDay(new Date(meeting.start_date), date)
    );
  };

  const getMeetingCount = (date: Date) => {
    return meetings.filter(meeting => 
      isSameDay(new Date(meeting.start_date), date)
    ).length;
  };

  const previousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">
          {format(currentMonth, 'MMMM yyyy', { locale: fr })}
        </h3>
        <div className="flex items-center space-x-1">
          <button
            onClick={previousMonth}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Mois précédent"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={nextMonth}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Mois suivant"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Days of week */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, index) => (
          <div key={index} className="text-center text-xs font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isSelected = isSameDay(day, selectedDate);
          const isCurrentDay = isToday(day);
          const dayHasMeeting = hasMeeting(day);
          const meetingCount = getMeetingCount(day);

          return (
            <button
              key={day.toISOString()}
              onClick={() => onDateSelect(day)}
              disabled={!isCurrentMonth}
              className={`
                relative p-2 text-sm rounded-md transition-colors
                ${!isCurrentMonth 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-900 hover:bg-gray-100'
                }
                ${isSelected 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : ''
                }
                ${isCurrentDay && !isSelected 
                  ? 'bg-blue-50 text-blue-600 font-semibold' 
                  : ''
                }
              `}
            >
              <span className="block">{format(day, 'd')}</span>
              {dayHasMeeting && isCurrentMonth && (
                <div className={`
                  absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full
                  ${isSelected ? 'bg-white' : 'bg-blue-600'}
                `}>
                  {meetingCount > 1 && (
                    <span className="absolute -top-1 -right-1 text-xs bg-red-500 text-white rounded-full w-3 h-3 flex items-center justify-center">
                      {meetingCount}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full" />
            <span>Rendez-vous</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-50 border border-blue-200 rounded-full" />
            <span>Aujourd'hui</span>
          </div>
        </div>
      </div>
    </div>
  );
};